/**
 * QuestCard — Daily AI Quest with Verify/Discard/Unsure actions
 * Text-only (no AI images). Strong disclosure on every card.
 */
import { useState } from 'react';
import { Sparkles, Camera, X, AlertTriangle, Upload, CheckCircle, Flag } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BADGE_STYLES = {
  Quest: 'bg-[#FF6B35]',
  Challenge: 'bg-purple-500',
  'AI Pick': 'bg-[#F4C430] text-gray-900',
};

const QuestCard = ({ quest, onResponded, onVerifyWithModal }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showVerify, setShowVerify] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [responded, setResponded] = useState(null); // { action, nudge, badge }
  const [showReport, setShowReport] = useState(false);

  const handleRespond = async (action) => {
    if (!user) { navigate('/profile'); return; }
    if (action === 'verify') {
      // Open unified FoodCard modal with quest context
      onVerifyWithModal?.({
        quest_id: quest.quest_id,
        quest_title: quest.title,
        quest_blurb: quest.blurb,
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('fq_auth_token');
      const res = await fetch(`${API_URL}/api/quests/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ quest_id: quest.quest_id, action, photo_url: photoUrl || undefined, notes: notes || undefined }),
      });
      if (res.ok) {
        const data = await res.json();
        setResponded({ action, nudge: data.nudge, badge: data.badge });
        setShowVerify(false);
        onResponded?.(quest.quest_id, action);
      }
    } catch {} finally { setLoading(false); }
  };

  if (responded) {
    return (
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-[20px] p-5 text-white h-[220px] flex flex-col justify-between" data-testid={`quest-responded-${quest.quest_id}`}>
        <div>
          <div className="flex items-center gap-2 mb-3">
            {responded.action === 'verify' && <CheckCircle className="w-5 h-5 text-emerald-400" />}
            {responded.action === 'discard' && <X className="w-5 h-5 text-gray-400" />}
            {responded.action === 'unsure' && <AlertTriangle className="w-5 h-5 text-amber-400" />}
            <span className="font-bold text-sm">
              {responded.action === 'verify' ? 'Verified!' : responded.action === 'discard' ? 'Skipped' : 'Shared as AI Idea'}
            </span>
            {responded.badge === 'verified' && <span className="bg-emerald-500 text-[9px] font-bold px-2 py-0.5 rounded-full">Verified</span>}
            {responded.badge === 'unverified' && <span className="bg-gray-500 text-[9px] font-bold px-2 py-0.5 rounded-full">AI Generated – Untested</span>}
          </div>
          <p className="text-sm text-white/70 leading-relaxed">{responded.nudge}</p>
        </div>
        <p className="text-[10px] text-white/30">{quest.title}</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-[20px] p-5 text-white flex flex-col justify-between relative" style={{ minHeight: '220px' }} data-testid={`quest-card-${quest.quest_id || ''}`}>
      {/* Report button */}
      <button onClick={() => setShowReport(!showReport)} className="absolute top-3 right-3 p-1.5 text-white/30 hover:text-white/60 transition-colors" data-testid="quest-report-btn">
        <Flag className="w-3.5 h-3.5" />
      </button>
      {showReport && (
        <div className="absolute top-10 right-3 bg-white text-gray-900 rounded-xl shadow-xl p-3 z-10 w-48 text-xs">
          <p className="font-semibold mb-1">Report mismatch</p>
          <p className="text-gray-500 mb-2">Flag this quest as inappropriate or misleading</p>
          <button onClick={() => { setShowReport(false); }} className="w-full py-1.5 bg-red-50 text-red-600 rounded-lg font-medium hover:bg-red-100">Report</button>
        </div>
      )}

      {/* Tag + Title */}
      <div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full text-white ${BADGE_STYLES[quest.tag] || 'bg-[#FF6B35]'}`}>{quest.tag || 'Quest'}</span>
        <h3 className="text-base font-bold mt-3 mb-1.5 leading-tight">{quest.title}</h3>
        <p className="text-xs text-white/60 line-clamp-2 leading-relaxed">{quest.blurb}</p>
      </div>

      {/* Disclosure */}
      <p className="text-[9px] text-white/30 mt-2 leading-relaxed italic">
        {quest.disclosure || "AI-suggested idea only – not representative of any real dish. Verify with your own experience!"}
      </p>

      {/* Verify photo modal */}
      {showVerify && (
        <div className="mt-3 bg-white/10 rounded-xl p-3 space-y-2">
          <p className="text-xs text-white/80 font-medium">Upload a real photo to verify:</p>
          <input type="url" value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} placeholder="Paste photo URL..." className="w-full px-3 py-2 bg-white/10 rounded-lg text-xs text-white placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-[#FF6B35]" />
          <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add notes (optional)..." className="w-full px-3 py-2 bg-white/10 rounded-lg text-xs text-white placeholder-white/40 focus:outline-none" />
          <div className="flex gap-2">
            <button onClick={() => handleRespond('verify')} disabled={!photoUrl || loading} className="flex-1 py-2 bg-emerald-500 text-white text-xs font-bold rounded-lg disabled:opacity-50 flex items-center justify-center gap-1">
              <Upload className="w-3 h-3" />{loading ? 'Verifying...' : 'Submit Verification'}
            </button>
            <button onClick={() => setShowVerify(false)} className="px-3 py-2 bg-white/10 text-white/60 text-xs rounded-lg">Cancel</button>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {!showVerify && (
        <div className="flex gap-2 mt-3">
          <button onClick={() => handleRespond('verify')} disabled={loading} className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 active:scale-95" data-testid="quest-verify-btn">
            <Camera className="w-3.5 h-3.5" />Verify & Repost
          </button>
          <button onClick={() => handleRespond('discard')} disabled={loading} className="px-3 py-2.5 bg-white/10 hover:bg-white/20 text-white/60 text-xs font-medium rounded-xl transition-all" data-testid="quest-discard-btn">
            Skip
          </button>
          <button onClick={() => handleRespond('unsure')} disabled={loading} className="px-3 py-2.5 bg-white/10 hover:bg-white/20 text-white/60 text-xs font-medium rounded-xl transition-all" data-testid="quest-unsure-btn">
            Unsure
          </button>
        </div>
      )}

      {/* AI attribution */}
      <div className="flex items-center gap-1.5 mt-2">
        <Sparkles className="w-3 h-3 text-[#F4C430]" />
        <span className="text-[10px] text-white/40">AI-generated · Tap to try</span>
      </div>
    </div>
  );
};

export default QuestCard;
